'use client';

import { createContext, lazy, PropsWithChildren, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages } from 'react-intl';
import logGarminConnectModalViewed from '@alltrails/amplitude/events/logGarminConnectModalViewed';
import logGarminConnectSucceeded from '@alltrails/amplitude/events/logGarminConnectSucceeded';
import logGarminRouteSent from '@alltrails/amplitude/events/logGarminRouteSent';
import logGarminSendRouteTapped from '@alltrails/amplitude/events/logGarminSendRouteTapped';
import logUpsellShown from '@alltrails/amplitude/events/logUpsellShown';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import logError from '@alltrails/core/utils/logError';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Toast from '@alltrails/denali/components/Toast';
import type GarminCourse from '@alltrails/types/GarminCourse';
import { courseImport, GarminPermissionTypes, getGarminPermissions, postGarminCourse } from '../../utils/garminRequests';
import { garminNewConnectionKey, garminPermissionsReturnKey, useRemoveQueryParam } from '../../utils/modalHelpers';
import garminImageSrc from '../GarminStep/garmin-alltrails.png';
const GarminInformationalModal = lazy(() => import('../GarminInformationalModal'));
const GarminEnableCoursesModal = lazy(() => import('../GarminEnableCoursesModal'));
const GarminRouteSentModal = lazy(() => import('../GarminRouteSentModal'));
const GarminUpdateCourseModal = lazy(() => import('../GarminUpdateCourseModal'));
const GarminRouteSentErrorModal = lazy(() => import('../GarminRouteSentErrorModal'));

const messages = defineMessages({
  SENDING_ROUTE: { defaultMessage: 'Sending to Garmin...' }
});

type GarminValue = {
  canUpdateCourse: boolean;
  handleSendRoute?: () => void;
  handleUpdateRoute?: () => void;
  // allows consumers to set the map id, as in cases when a new activity or map are created and we do not get this prop from rails:
  setCurrentMapId?: (mapId: number) => void;
};

type Props = {
  mapId?: number;
  garminCourse?: GarminCourse;
};

const initialGarminContext: GarminValue = {
  canUpdateCourse: false,
  handleSendRoute: undefined,
  handleUpdateRoute: undefined,
  setCurrentMapId: undefined
};

const GarminContext = createContext<GarminValue>(initialGarminContext);

const GarminProvider = ({ mapId, garminCourse, children }: PropsWithChildren<Props>) => {
  const {
    formattedDefaultMessages: { SENDING_ROUTE }
  } = useFormatMessage(messages);
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const [showInformationalModal, setShowInformationalModal] = useState(false);
  const [showEnableCoursesModal, setShowEnableCoursesModal] = useState(false);
  const [showRouteSentModal, setShowRouteSentModal] = useState(false);
  const [showUpdateCourseModal, setShowUpdateCourseModal] = useState(false);
  const [showRouteSentErrorModal, setShowRouteSentErrorModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [currentMapId, setCurrentMapId] = useState<number | undefined>(mapId);
  const [isLoading, setIsLoading] = useState(false);
  const removeQueryParam = useRemoveQueryParam();

  const permissionsIncludeCourseImport = useCallback((permissions: GarminPermissionTypes[]) => Boolean(permissions?.includes(courseImport)), []);

  const sendRoute = useCallback(async () => {
    if (!currentMapId) {
      return;
    }

    let isMounted = true;
    const timer = setTimeout(() => {
      setToastMessage(SENDING_ROUTE);
    }, 1000);

    postGarminCourse(currentMapId)
      .then(() => {
        if (isMounted) {
          clearTimeout(timer);
          setToastMessage(null);
          setShowRouteSentModal(true);
        }
      })
      .catch(err => {
        logError(new Error('Error when updating garmin course', { cause: err }));

        if (isMounted) {
          clearTimeout(timer);
          setToastMessage(null);
          setShowRouteSentErrorModal(true);
        }
      });

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [SENDING_ROUTE, currentMapId]);

  const fetchGarminPermissions = useCallback(async () => {
    await getGarminPermissions()
      .then(res => {
        if (permissionsIncludeCourseImport(res?.permissions)) {
          logGarminRouteSent({});
          sendRoute();
        } else {
          logGarminConnectModalViewed({ modal_type: 'garmin courses' });
          setShowEnableCoursesModal(true);
        }
      })
      .catch(err => {
        logError(new Error('Error when fetching garmin permissions', { cause: err }));
      });
  }, [permissionsIncludeCourseImport, sendRoute]);

  const handleSendRoute = useCallback(() => {
    logGarminSendRouteTapped({ is_update: Boolean(garminCourse) });
    setShowUpdateCourseModal(false);

    if (!isSubscriber(user)) {
      logUpsellShown({ trigger: 'Send Routes To Garmin' });
      window.location.assign(wrapUrlSafe('/plus', languageRegionCode));
      return;
    }

    if (!user?.garminConnected) {
      logGarminConnectModalViewed({ modal_type: 'connect' });
      setShowInformationalModal(true);
      return;
    }

    fetchGarminPermissions();
  }, [garminCourse, user, fetchGarminPermissions, languageRegionCode]);

  const handleUpdateRoute = useCallback(() => setShowUpdateCourseModal(true), []);

  const handleCloseRouteSentModal = () => {
    // indicate to the user that their action was successful
    setIsLoading(true);
    // we need to reload the page in order to refetch the garmin course
    window.location.reload?.();
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const userWasRedirectedAfterAuth = searchParams?.has(garminPermissionsReturnKey);
    const userJustConnected = searchParams?.has(garminNewConnectionKey);
    const shouldRefetchPermissions = isSubscriber(user) && user?.garminConnected;

    if (userJustConnected) {
      logGarminConnectSucceeded({});
      removeQueryParam(garminNewConnectionKey);
    }

    if (userWasRedirectedAfterAuth) {
      removeQueryParam(garminPermissionsReturnKey);

      if (shouldRefetchPermissions) {
        fetchGarminPermissions();
      }
    }
    // we only want to run this on mount as we are checking to see if the user was redirected after auth
  }, []);

  const value = useMemo(
    () => ({
      canUpdateCourse: Boolean(garminCourse?.updated_at),
      handleSendRoute,
      handleUpdateRoute,
      setCurrentMapId
    }),
    [garminCourse?.updated_at, handleSendRoute, handleUpdateRoute]
  );

  return (
    <GarminContext.Provider value={value}>
      <Toast isOpen={Boolean(toastMessage)} message={toastMessage} type="neutral" />
      {showInformationalModal && (
        <Suspense fallback={null}>
          <GarminInformationalModal
            isOpen={showInformationalModal}
            onCloseRequest={() => setShowInformationalModal(false)}
            imageSrc={garminImageSrc}
          />
        </Suspense>
      )}
      {showEnableCoursesModal && (
        <Suspense fallback={null}>
          <GarminEnableCoursesModal
            handleCloseModal={() => {
              setShowEnableCoursesModal(false);
            }}
          />
        </Suspense>
      )}
      {showRouteSentModal && (
        <Suspense fallback={null}>
          <GarminRouteSentModal isOpen={showRouteSentModal} onCloseRequest={handleCloseRouteSentModal} isLoading={isLoading} />
        </Suspense>
      )}
      {showUpdateCourseModal && garminCourse && (
        <Suspense fallback={null}>
          <GarminUpdateCourseModal
            lastUpdatedTime={garminCourse.updated_at}
            handleUpdate={handleSendRoute}
            onRequestClose={() => setShowUpdateCourseModal(false)}
          />
        </Suspense>
      )}
      {showRouteSentErrorModal && (
        <Suspense fallback={null}>
          <GarminRouteSentErrorModal
            onRequestClose={() => {
              setShowRouteSentErrorModal(false);
            }}
          />
        </Suspense>
      )}
      {children}
    </GarminContext.Provider>
  );
};

export { GarminContext, GarminProvider };
export type { GarminValue };
